import React, { useState } from 'react';
import { Database, Download, Upload, AlertCircle } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { supabase } from '../../lib/supabase';

export default function AdminBackup() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleBackup = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Buscar dados de todas as tabelas
      const [
        { data: events },
        { data: smallGroups },
        { data: ministries },
        { data: prayerRequests },
        { data: messages },
        { data: liveStreams }
      ] = await Promise.all([
        supabase.from('events').select('*'),
        supabase.from('small_groups').select('*'),
        supabase.from('ministries').select('*'),
        supabase.from('prayer_requests').select('*'),
        supabase.from('messages').select('*'),
        supabase.from('live_streams').select('*')
      ]);

      // Criar objeto de backup
      const backup = {
        timestamp: new Date().toISOString(),
        data: {
          events,
          small_groups: smallGroups,
          ministries,
          prayer_requests: prayerRequests,
          messages,
          live_streams: liveStreams
        }
      };

      // Criar arquivo de backup
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSuccess('Backup realizado com sucesso!');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout title="Backup e Restauração">
      <div className="space-y-6">
        {/* Backup */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <Database className="h-6 w-6 text-brand-teal" />
            <h2 className="text-xl font-semibold text-brand-teal">Backup do Sistema</h2>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 text-red-600 rounded-lg flex items-start">
              <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 text-green-600 rounded-lg">
              {success}
            </div>
          )}

          <div className="grid gap-6 md:grid-cols-2">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">Fazer Backup</h3>
              <p className="text-gray-600 mb-4">
                Faça o download de todos os dados do sistema em um arquivo JSON.
              </p>
              <button
                onClick={handleBackup}
                disabled={loading}
                className="bg-brand-teal text-white px-4 py-2 rounded-lg hover:bg-brand-teal/90 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                {loading ? 'Gerando backup...' : 'Baixar Backup'}
              </button>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">Restaurar Backup</h3>
              <p className="text-gray-600 mb-4">
                Restaure os dados do sistema a partir de um arquivo de backup.
              </p>
              <label className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-2 w-fit">
                <Upload className="h-4 w-4" />
                Selecionar Arquivo
                <input
                  type="file"
                  accept=".json"
                  className="hidden"
                  onChange={(e) => {
                    // Implementar restauração
                    alert('Funcionalidade em desenvolvimento');
                  }}
                />
              </label>
            </div>
          </div>
        </div>

        {/* Configurações de Backup */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold text-brand-teal mb-4">Configurações de Backup</h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h3 className="font-medium">Backup Automático</h3>
                <p className="text-sm text-gray-600">Realizar backup diário do banco de dados</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" defaultChecked />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-light rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-teal"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h3 className="font-medium">Retenção de Backups</h3>
                <p className="text-sm text-gray-600">Período de retenção dos backups automáticos</p>
              </div>
              <select className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal">
                <option>7 dias</option>
                <option>30 dias</option>
                <option>90 dias</option>
                <option>365 dias</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}